import React from "react";
import styles from "./BetTableCmeter.module.css";

// Helper function to map card name to image path
const getCardImage = (cardName) => {
  const cardMap = {
    'A': '/assets/img/aa2/A.png',
    '1': '/assets/img/aa2/A.png',
    '2': '/assets/img/aa2/2.png',
    '3': '/assets/img/aa2/3.png',
    '4': '/assets/img/aa2/4.png',
    '5': '/assets/img/aa2/5.png',
    '6': '/assets/img/aa2/6.png',
    '7': '/assets/img/aa2/7.png',
    '8': '/assets/img/aa2/8.png',
    '9': '/assets/img/aa2/9.png',
    '10': '/assets/img/aa2/10.png',
    'J': '/assets/img/aa2/J.png',
    'Q': '/assets/img/aa2/Q.png',
    'K': '/assets/img/aa2/K.png'
  };
  return cardMap[cardName] || '/assets/img/aa2/A.png';
};

// Helper function to extract card value from card code (e.g., "2SS" -> "2", "QDD" -> "Q")
const extractCardValue = (cardCode) => {
  if (!cardCode) return null;
  const value = cardCode.replace(/(DD|SS|CC|HH)$/, '').trim();
  return value || null;
};

// Helper function to parse cards and categorize into Low (1-9) and High (10-K)
const parseCards = (cardString) => {
  if (!cardString) return { lowCards: [], highCards: [], lowCount: 0, highCount: 0 };
  
  const cards = cardString.split(',');
  const lowCards = [];
  const highCards = [];
  
  cards.forEach((cardCode) => {
    const trimmedCode = cardCode.trim();
    const value = extractCardValue(trimmedCode);
    if (!value) return;
    
    // Use full card code for image path (e.g., "2SS.png", "QDD.png")
    const cardImage = `/assets/img/cards/${trimmedCode}.png`;
    
    // Low cards: A, 2-9
    if (value === 'A' || value === '1' || (parseInt(value) >= 2 && parseInt(value) <= 9)) {
      lowCards.push({ code: trimmedCode, value, image: cardImage });
    }
    // High cards: 10, J, Q, K
    else if (value === '10' || value === 'J' || value === 'Q' || value === 'K') {
      highCards.push({ code: trimmedCode, value, image: cardImage });
    }
  });
  
  return { lowCards, highCards };
};

// Helper function to parse rdesc for counts (format: "40,34,0,0" -> lowCount: 40, highCount: 34)
const parseRdesc = (rdesc) => {
  if (!rdesc) return { lowCount: 0, highCount: 0 };
  const parts = rdesc.split(',');
  return {
    lowCount: parseInt(parts[0]) || 0,
    highCount: parseInt(parts[1]) || 0,
  };
};

// Helper to normalize string for matching
const normalizeString = (str) => {
  if (!str) return "";
  return str.replace(/\s+/g, ' ').trim().toLowerCase();
};

// Helper to create all exposure key variations
const createExposureKeys = (selection) => {
  if (!selection) return [];
  const keys = [
    selection,
    selection.toLowerCase(),
    selection.toLowerCase().trim(),
    selection.trim(),
    normalizeString(selection),
  ];
  return [...new Set(keys.filter(k => k))];
};

const BetTableCmeter = ({ data = [], exposures = {}, gameData = {} }) => {
  // Helper function to find bet data by name
  const findBetData = (name) => {
    return data.find((item) => item.nat === name) || { nat: name, b: 0, sid: null, gstatus: "SUSPENDED", min: 10, max: 25000 };
  };

  // Check if bet is suspended
  const isSuspended = (betData) => {
    return betData?.gstatus === "SUSPENDED" || betData?.b === 0;
  };

  // Get exposure value for a bet
  const getExposure = (betName) => {
    if (!exposures || Object.keys(exposures).length === 0) {
      return 0;
    }

    const keys = createExposureKeys(betName);
    for (const key of keys) {
      if (key && exposures[key] !== undefined && exposures[key] !== null) {
        const expValue = parseFloat(exposures[key]);
        if (!isNaN(expValue) && expValue !== 0) {
          return expValue;
        }
      }
    }
    return 0;
  };

  // Format exposure value
  const formatExposure = (value) => {
    if (!value || value === 0) return "0";
    return value < 0 ? value.toFixed(0) : `+${value.toFixed(0)}`;
  };

  // Format min/max values
  const formatLimit = (value) => {
    if (!value) return "10.0";
    if (value >= 1000) {
      return `${(value / 1000).toFixed(0)}K`;
    }
    return value.toFixed(1);
  };

  // Get card string and rdesc from gameData
  const cardString = gameData?.card || "";
  const rdesc = gameData?.rdesc || "";
  
  const { lowCards, highCards } = parseCards(cardString);
  const { lowCount, highCount } = parseRdesc(rdesc);

  const lowBet = findBetData("Low");
  const highBet = findBetData("High");

  const lowExposure = getExposure("Low");
  const highExposure = getExposure("High");

  return (
    <div>
      {/* Low Card Section */}
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' , gap: '5px' }} className={`${styles.meterLhCardContainer} mt-1`}>
        <h5 className="d-inline-block mb-0">
          <b>Low</b> <span className="text-primary ml-1">{lowCount || 0}</span>
        </h5>
        <div className={`text-center ${styles.meterLhCard}`}>
          {lowCards.map((card, index) => (
            card.image && (
              <img key={index} src={card.image} alt={card.value} className="mr-2" />
            )
          ))}
        </div>
      </div>
      <span className="text-right" style={{ color: 'black' }}>{formatExposure(lowExposure)}</span>

      {/* High Card Section */}
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' , gap: '5px' }} className={`${styles.meterLhCardContainer} mt-1`}>
        <h5 className="d-inline-block mb-0">
          <b>High</b> <span className="text-primary ml-1">{highCount || 0}</span>
        </h5>
        <div className={`text-center ${styles.meterLhCard}`}>
          {highCards.map((card, index) => (
            card.image && (
              <img key={index} src={card.image} alt={card.value} className="mr-2" />
            )
          ))}
        </div>
      </div>
      <span className="d-block text-right" style={{ color: 'black' }}>{formatExposure(highExposure)}</span>

    </div>
  );
};

export default BetTableCmeter;

